import axios, { type AxiosInstance } from 'axios';
import { ref } from 'vue';
import { gatewayClients } from '@tfpl/api-client';
import { useKioskAuth } from '@/composables/useKioskAuth';
import { TFPL_SERVICE_ACCOUNT_KEY_HEADER } from '@/constants';

/**
 * Axios interceptor for kiosk mode. Attaches the persisted kiosk service-account
 * key to every outgoing request instead of the Firebase ID token used by the
 * normal interactive flow.
 *
 * It must cover BOTH axios worlds the kiosk pages use:
 *  - the global `axios` (hand-rolled kiosk calls: pairing start/poll, self-config)
 *  - every `@tfpl/api-client` gateway client instance (hubClient / agwClient / …
 *    used by the data pages), each of which has its OWN axios instance. Those
 *    instances only carry the (empty in kiosk mode) `tfpl-id-token` interceptor
 *    from `configureBrowserClients`, so without this they'd send no SA key and
 *    the gateway would reject every call with 401/403.
 *
 * Idempotent — calling setup twice keeps a single live interceptor per instance.
 */
const authTargets = (): AxiosInstance[] => [
  axios as unknown as AxiosInstance,
  ...Object.values(gatewayClients).map((client) => client.instance),
];

const readSaKey = (headers: unknown): string | undefined => {
  if (!headers || typeof headers !== 'object') return undefined;
  const h = headers as { get?: (name: string) => unknown; [key: string]: unknown };
  if (typeof h.get === 'function') {
    return (h.get(TFPL_SERVICE_ACCOUNT_KEY_HEADER) as string | undefined) ?? undefined;
  }
  return h[TFPL_SERVICE_ACCOUNT_KEY_HEADER] as string | undefined;
};

export const useKioskInterceptor = () => {
  const { kioskKey, clearKioskKey } = useKioskAuth();

  // One interceptor id per axios instance we hook into.
  const requestInterceptors = ref<Array<{ instance: AxiosInstance; id: number }>>([]);
  const unauthorizedInterceptors = ref<Array<{ instance: AxiosInstance; id: number }>>([]);

  const removeKioskAuthInterceptor = () => {
    for (const { instance, id } of requestInterceptors.value) {
      instance.interceptors.request.eject(id);
    }
    requestInterceptors.value = [];
  };

  const removeKioskUnauthorizedInterceptor = () => {
    for (const { instance, id } of unauthorizedInterceptors.value) {
      instance.interceptors.response.eject(id);
    }
    unauthorizedInterceptors.value = [];
  };

  const setupKioskAuthInterceptor = () => {
    removeKioskAuthInterceptor();

    for (const instance of authTargets()) {
      const id = instance.interceptors.request.use(
        (config) => {
          if (kioskKey.value) {
            config.headers.set(TFPL_SERVICE_ACCOUNT_KEY_HEADER, kioskKey.value);
          }
          return config;
        },
        (error) => Promise.reject(error),
      );
      requestInterceptors.value.push({ instance, id });
    }
  };

  const setupKioskUnauthorizedInterceptor = () => {
    removeKioskUnauthorizedInterceptor();

    for (const instance of authTargets()) {
      const id = instance.interceptors.response.use(
        (response) => response,
        (error) => {
          const status = error?.response?.status;
          if (status !== 401 && status !== 403) {
            throw error;
          }
          const sentKey = readSaKey(error.config?.headers);
          if (!sentKey || sentKey !== kioskKey.value) {
            // Failure wasn't from a request authenticated with OUR key
            // (e.g. a transient page-mount fetch). Stay paired.
            throw error;
          }
          if (status === 403) {
            // 403 = identity OK but no permission for this endpoint.
            // Don't kick the kiosk out — the SA may still have access
            // to other pages, and the displayed page can degrade
            // gracefully (empty list / error banner).
            console.warn(
              '[kiosk] 403 on a kiosk-authenticated request — page is missing permission, key kept',
              error.config?.url,
            );
            throw error;
          }
          // status === 401 → AGW says the key itself is invalid
          // (bad/deactivated/IP-blocked). Drop it so KioskLayout
          // bounces back to /kiosk/pairing for re-pair.
          console.warn('[kiosk] 401 on a kiosk-authenticated request — clearing stored key');
          clearKioskKey();
          throw error;
        },
      );
      unauthorizedInterceptors.value.push({ instance, id });
    }
  };

  return {
    setupKioskAuthInterceptor,
    setupKioskUnauthorizedInterceptor,
    removeKioskAuthInterceptor,
    removeKioskUnauthorizedInterceptor,
  };
};
